import React from "react";
import { format } from "date-fns";
import { IconButton } from "@mui/material";
import RemoveRedEyeIcon from "@mui/icons-material/RemoveRedEye";
import EditIcon from "@mui/icons-material/Edit";
import { REQUIRED_COLUMNS, MAX_ROWS, MAX_FILE_SIZE } from "../constants";
import Papa from "papaparse";

export const convertTimestampToHumanReadable = (timestamp) => {
  const date = new Date(timestamp);
  return format(date, "yyyy-MM-dd HH:mm:ss") + " UTC";
};

export const disableLink = (record) => {
  return record.request_status === "In-Progress" ? null : record.signed_url;
};

export const activityLogsGridColumns = {
  created: "Created",
  customer_id: "Customer ID",
  action: "Action",
  details: "Details",
  resource_type: "Resource Type",
  actor: "Actor"
};

export const activityLogsOptionalColumns = {
  unique_id: "Log ID",
  resource_id: "Resource ID",
};

export const pdfGridColumns = {
  request_timestamp: "Timestamp",
  campaign: "Campaign",
  current_contacts: "Contacts",
  custom_template_name: "Saved Template",
  delivery_method: "Delivery Method",
  request_status: "Status",
  status_updated: "Status Updated",
  conditional_url: "File",
  credit_cost: 'Credit Cost',
  credit_charged_base: 'Base Credit',
  credit_charged_extra: 'Extra Credit'
};

export const adminPdfGridColumns = {
  edit: "Actions",
  request_timestamp: "Timestamp",
  customer_id: "Customer ID",
  campaign: "Campaign",
  current_contacts: "Contacts",
  custom_template_name: "Saved Template",
  delivery_method: "Delivery Method",
  request_status: "Status",
  status_updated: "Status Updated",
  conditional_url: "File",
  credit_cost: 'Credit Cost',
  credit_charged_base: 'Base Credit',
  credit_charged_extra: 'Extra Credit'
};

export const adminPdfOptionalColumns = {
  unique_id: "Unique ID"
}

export const campaignGridColumns = {
  campaign_name: "Campaign",
  total: "#Contacts",
};

export const respondedGridColumns = {
  greeting_name: "Full Name",
  "ma-addr_line1": "Street",
};

export const contactVisitsGridColumns = {
  visited: "visited",
  trackable: "qrcode-id",
  campaign: "campaign",
};

export const existingUsersGridColumns = {
  created: "Created",
  organization: "Organization",
  user_name: "User Name",
  email: "Email",
  phone: "Phone",
  subscription_plan: "Subscription Plan",
  subscription_start_date: "Subscription Start Date",
  subscription_end_date: "Subscription End Date",
  subscription_status: "Subscription Status",
  billing_cycle: "Billing Cycle",
  payment_status: "Payment Status",
  vendors: "Partners",
  confirmation_status: 'Confirmation Status',
  campaign_quota_used: 'Campaigns',
  contact_quota_used: 'Contacts',
  template_quota_used: 'Templates',
  collaterals: 'Collaterals',
  envelopes: 'Envelopes',
  request_quota_used: 'PDF Requests',
  credit_limit: 'Credit Limit',
  credit_used: 'Credit Used',
  extra_credit_limit: 'Extra Credit Limit',
  extra_credit_used: 'Extra Credit Used'
};

export const usersGridOptionalColumns = {
  customer_id: "Customer ID",
  tpx_id: "Tracking Pixel",
  user_id: "User ID",
  cognito_pool_id: "Cognito Pool ID",
  role: "Role",
  campaign_quota_limit: 'Campaign Quota',
  contact_quota_limit: 'Contact Quota',
  template_quota_limit: 'Template Quota',
  request_quota_limit: 'PDF Request Quota',
};

export const savedTemplatesGridColumns = {
  view: 'View',
  choice: 'Template',
  template_name: 'Saved Template',
  created: 'Created',
  version: 'Version',
  status: 'Status',
  owner: "Owner",
  type: "Type"
}

export const generateGridColumns = (
  mappingConfig,
  alwaysVisibleColumn,
  onView, // New parameter for row-level actions
  onEdit,// New parameter for edit action
  optionalColumns = {}
) => {
  const mainColumns = Object.keys(mappingConfig).map((key) => ({
    field: key,
    headerName: mappingConfig[key],
    flex: key === 'view' || key === 'edit' ? 0.3 : 1,
    minWidth: key === 'view' || key === 'edit' ? 70 : 150,
    hide: false,
    hideable: key !== alwaysVisibleColumn && key !== 'view' && key !== 'edit',
    sortable: key !== 'view' && key !== 'edit',
    filterable: key !== 'view' && key !== 'edit',
    renderCell: (() => {
      if (key === "edit" && onEdit) {
        return (params) => (
          <IconButton
            size="small"
            color="primary"
            onClick={(e) => {
              onEdit(params.row);
            }}
          >
            <EditIcon fontSize="small" />
          </IconButton>
        );
      }
      if (key === "view" && onView) {
        return (params) => (
          <IconButton
            size="small"
            color="primary"
            onClick={(e) => {
              onView(params.row);
            }}
          >
            <RemoveRedEyeIcon fontSize="small" />
          </IconButton>
        );
      }
      if (key === "conditional_url") {
        return (params) => {
          if (!params.value) return <span>None</span>;
          const urls = typeof params.value === "string"
            ? params.value.split(",").map(u => u.trim()).filter(u => u)
            : [params.value];

          if (urls.length === 0) return <span>None</span>;

          const collateralType = params.row.collateral_type || "None";
          const types = collateralType.split(",").map(t => t.trim());

          if (urls.length === 1) {
            return (
              <a
                href={urls[0]}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => {
                  e.stopPropagation();
                }}
                style={{ textDecoration: "none", color: "#1976d2", fontWeight: 500 }}
              >
                {collateralType !== "None" ? collateralType : "None"}
              </a>
            );
          }

          return (
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              {urls.map((url, index) => (
                <React.Fragment key={index}>
                  <a
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => {
                      e.stopPropagation();
                    }}
                    style={{ textDecoration: "none", color: "#1976d2", fontWeight: 500 }}
                  >
                    {types[index] || "None"}
                  </a>
                  {index < urls.length - 1 && <span style={{ color: '#ccc' }}>|</span>}
                </React.Fragment>
              ))}
            </div>
          );
        };
      }
      if (key === "details") {
        return (params) => {
          if (typeof params.value !== "string") return params.value;
          const parts = params.value.split(" -> ");
          if (parts.length === 2) {
            return (
              <span style={{ display: 'flex', alignItems: 'center', gap: '4px', flexShrink: 0, width: 'max-content' }}>
                {parts[0]}
                <span style={{ color: '#000', fontWeight: 'bold', fontSize: '1.2em', lineHeight: 1 }}>
                  →
                </span>
                {parts[1]}
              </span>
            );
          }
          return params.value;
        };
      }

      return undefined;
    })(),
  }));

  const optionalCols = Object.keys(optionalColumns).map((key) => ({
    field: key,
    headerName: optionalColumns[key],
    flex: 1,
    minWidth: 150,
    hide: true,
    hideable: true,
  }));
  return [...mainColumns, ...optionalCols];
};

export const pdfRequestRowMapping = {
  request_timestamp: "request_timestamp",
  campaign: "campaign",
  current_contacts: "current_contacts",
  custom_template_name: "custom_template_name",
  delivery_method: "delivery_method",
  request_status: "request_status",
  conditional_url: "conditional_url",
  unique_id: "unique_id",
  status_updated: "status_updated",
  credit_cost: "credit_cost",
  credit_charged_base: "credit_charged_base",
  credit_charged_extra: "credit_charged_extra"
};

export const adminPdfRequestRowMapping = {
  edit: "edit",
  request_timestamp: "request_timestamp",
  customer_id: "customer_id",
  campaign: "campaign",
  current_contacts: "current_contacts",
  custom_template_name: "custom_template_name",
  delivery_method: "delivery_method",
  request_status: "request_status",
  conditional_url: "signed_url",
  unique_id: "unique_id",
  status_updated: "status_updated",
  credit_cost: "credit_cost",
  credit_charged_base: "credit_charged_base",
  credit_charged_extra: "credit_charged_extra"
};


export const activityLogsRowMapping = {
  unique_id: "unique_id",
  resource_type: "resource_type",
  resource_id: "resource_id",
  customer_id: "customer_id",
  action: "action",
  details: "details",
  actor: "actor",
  created: "created",
  unique_id: "unique_id",
}

export const campaignRowMapping = {
  campaign_name: "campaign_name",
  total: "total",
};
export const respondedRowMapping = {
  greeting_name: "greeting_name",
  "ma-addr_line1": "ma-addr_line1",
};
export const contactVisitsRowMapping = {
  visited: "visited",
  trackable: "trackable",
  campaign: "campaign",
};
export const existingUsersRowMapping = {
  created: "created",
  organization: "organization",
  user_name: "user_name",
  email: "email",
  phone: "phone",
  subscription_plan: "subscription_plan",
  subscription_start_date: "subscription_start_date",
  subscription_end_date: "subscription_end_date",
  subscription_status: "subscription_status",
  billing_cycle: "billing_cycle",
  payment_status: "payment_status",
  vendors: "vendors",
  campaign_quota_used: 'campaign_quota_used',
  contact_quota_used: 'contact_quota_used',
  template_quota_used: 'template_quota_used',
  collaterals: 'collaterals',
  envelopes: 'envelopes',
  request_quota_used: 'request_quota_used',
  confirmation_status: 'confirmation_status',
  credit_limit: 'credit_limit',
  credit_used: 'credit_used',
  extra_credit_limit: 'extra_credit_limit',
  extra_credit_used: 'extra_credit_used',

  customer_id: "customer_id",
  tpx_id: "tpx_id",
  user_id: "user_id",
  cognito_pool_id: "cognito_pool_id",
  role: "role",
  campaign_quota_limit: 'campaign_quota_limit',
  contact_quota_limit: 'contact_quota_limit',
  template_quota_limit: 'template_quota_limit',
  collateral_quota_limit: 'collateral_quota_limit',
  envelope_quota_limit: 'envelope_quota_limit',
  request_quota_limit: 'request_quota_limit',
};

export const savedTemplatesRowMapping = {
  choice: 'choice',
  template_name: 'template_name',
  created: 'created',
  owner: 'owner',
  type: 'type',
  version: 'version',
  status: 'status'
}

export const generateGridRows = (data, mappingConfig, startIndex = 1) => {
  return data.map((record, index) => {
    const row = { id: startIndex + index };
    Object.keys(mappingConfig).forEach((key) => {
      const value = record[mappingConfig[key]];
      row[key] = Array.isArray(value) ? value.join(", ") : value;
    });
    // Add collateral_type if it exists in the record
    if (record.collateral_type) {
      row.collateral_type = record.collateral_type;
    }
    return row;
  });
};
export const campaignMapping = {
  campaign_name: "Campaign",
  total: "Contacts",
  created: "Created",
  destination_url: "Destination",
};
export const respondedMapping = {
  created: "created",
  trackable_url: "trackable_url",
  trackable: "qrcode-id",
  envelope_name: "envelope_name",
  greeting_name: "greeting_name",
  "ma-addr_line1": "ma-addr_line1",
  "ma-city": "ma-city",
  "ma-state": "ma-state",
  "ma-zip": "ma-zip",
  visits: "visits",
};

export const contactVisitsMapping = {
  visited: "visited",
  trackable: "qrcode-id",
  country: "country",
  user_agent: "user-agent",
  city: "city",
  region: "region",
};

export const activityLogsMapping = {
  created: "Created",
  customer_id: "Customer ID",
  actor: "Actor",
  action: "Action",
  details: "Details",
  resource_type: "Resource Type",
  resource_id: "Resource ID",
  unique_id: "Log ID",
}

export const generateFormData = (selectedRow, mappingConfig) => {
  return Object.keys(mappingConfig).map((key) => ({
    label: mappingConfig[key],
    value: selectedRow[key],
  }));
};

// validate the csv file. Returns an error message string, or null if valid
export const validateCsvContent = async (file) => {
  let parseResult;

  // 1. Check if the file is a CSV file
  if (file.type !== "text/csv") {
    return "Only CSV files are allowed.";
  }

  // 2. check file size exceeds 2MB
  if (file.size > MAX_FILE_SIZE) {
    return "File size should not exceed 2 MB.";
  }
  try {
    parseResult = await new Promise((resolve, reject) => {
      Papa.parse(file, {
        skipEmptyLines: false,
        complete: resolve,
        error: reject,
      });
    });
  } catch {
    return "Unable to read the CSV file.";
  }

  // 3. Check for parse errors
  const fatalErrors = (parseResult.errors || []).filter(
    (err) => err.code !== "UndetectableDelimiter"
  );
  if (fatalErrors.length > 0) {
    const firstError = fatalErrors[0];
    if (firstError?.message) {
      return `Invalid CSV file: ${firstError.message}`;
    }
  }

  const rawRows = parseResult.data || [];
  const lastContentIdx = rawRows.length - 1;

  const isBlankRow = (r) =>
    !Array.isArray(r) || r.every((v) => (v ?? "").toString().trim() === "");

  const allRows = rawRows.filter((r) => !isBlankRow(r));

  // 4. Check for empty csv file
  if (allRows.length === 0) return "The CSV file is empty.";

  const headers = allRows[0].map((h) => h.trim());
  const dataRows = allRows.slice(1);

  // 5. check csv has data rows
  if (dataRows.length === 0) return "The CSV file has no data rows.";

  // 6. check for delimiter 
  if (parseResult.meta.delimiter !== ',') return "The CSV file is not in the correct format. Please use comma separated values.";

  // 7. check csv has more than 25000 rows
  if (dataRows.length > MAX_ROWS) {
    return `Maximum allowed rows is ${MAX_ROWS}.\nThe CSV has ${dataRows.length} rows.`;
  }

  // 8. Check for blank lines 
  let blankLines = [];
  for (let i = 0; i <= lastContentIdx; i++) {
    if (isBlankRow(rawRows[i])) blankLines.push(i + 1);
  }

  if (blankLines.length > 0) {
    const shown = blankLines.join(", ");
    return `The CSV contains empty line(s) at line number(s): ${shown}.`;
  }

  // 9. Required columns present
  const missingCols = REQUIRED_COLUMNS.filter((c) => !headers.includes(c));
  if (missingCols.length > 0) {
    return `Missing required column(s): ${missingCols.join(", ")}.`;
  }

  // 10. No additional columns
  const extraCols = headers.filter((h) => h !== "" && !REQUIRED_COLUMNS.includes(h));
  if (extraCols.length > 0) {
    return `Only the required columns are allowed.\nUnexpected additional column(s): ${extraCols.join(", ")}.`;
  }

  // 11. Required columns must appear exactly once
  const duplicateRequired = REQUIRED_COLUMNS.filter(
    (c) => headers.filter((h) => h === c).length > 1
  );

  if (duplicateRequired.length > 0) {
    return `Each column must appear only once.\nDuplicate column(s) found: ${duplicateRequired.join(", ")}.`;
  }

  // 12. Blank or empty column headers
  const blankColIndices = [];
  headers.forEach((header, idx) => {
    if (!header) {
      blankColIndices.push(idx + 1);
    }
  });
  if (blankColIndices.length > 0) {
    return `Blank column name(s) found at position(s): ${blankColIndices.join(", ")}.`;
  }

  // 13. check csv has missing values in required columns
  const problems = [];
  dataRows.forEach((row, i) => {
    const missing = headers.filter(
      (_, k) => !(row[k] || "").trim()
    );
    if (missing.length > 0) {
      problems.push(`Row ${i + 1}: ${missing.join(", ")}`);
    }
  });

  if (problems.length > 0) {
    return `Missing values found:\n${problems.join("\n")}`;
  }

  return null;
};